/**
 * CRITICAL: BACKEND AUTH SERVICE - DO NOT MODIFY
 * 
 * This service handles all backend authentication with Lambda.
 * It is SEALED and immutable.
 * 
 * RULES:
 * 1. NO feature can modify this service
 * 2. ONLY uses AuthCore for token management
 * 3. NO external dependencies except fetch
 * 4. ALL requests must include proper auth headers
 */

import { AuthCore } from './AuthCore';

/**
 * Backend API response interface
 */
interface BackendResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Request configuration
 */
interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
  signal?: AbortSignal;
}

/**
 * IMMUTABLE BACKEND AUTH SERVICE
 * 
 * This class handles all authenticated requests to the backend.
 * It cannot be extended or modified.
 */
class BackendAuthService {
  private static instance: BackendAuthService | null = null;
  private readonly baseUrl: string;
  private constructor() {
    // FORCE CORRECT URL - Override environment variable if wrong
    const correctUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
    const envUrl = import.meta.env.VITE_API_BASE_URL;
    
    // Always use the correct URL, regardless of environment
    this.baseUrl = correctUrl;
    
    if (!this.baseUrl) {
      throw new Error('VITE_API_BASE_URL is not configured');
    }
  }

  /**
   * Singleton pattern
   */
  public static getInstance(): BackendAuthService {
    if (!BackendAuthService.instance) {
      BackendAuthService.instance = new BackendAuthService();
    }
    return BackendAuthService.instance;
  }
  
  /**
   * Reset singleton instance - for debugging only
   */
  public static resetInstance(): void {
    BackendAuthService.instance = null;
  }

  /**
   * Validate and sanitize input parameters
   */
  private validateInput(value: any, type: 'string' | 'number', fieldName: string): boolean {
    if (value === null || value === undefined) {
      throw new Error(`${fieldName} is required`);
    }
    
    if (type === 'string' && (typeof value !== 'string' || value.trim().length === 0)) {
      throw new Error(`${fieldName} must be a non-empty string`);
    }
    
    if (type === 'number' && (typeof value !== 'number' || value <= 0)) {
      throw new Error(`${fieldName} must be a positive number`);
    }
    
    return true;
  }

  /**
   * Sanitize URL endpoint
   */
  private sanitizeEndpoint(endpoint: string): string {
    if (!endpoint || typeof endpoint !== 'string') {
      throw new Error('Invalid endpoint');
    }
    
    // Remove any potential XSS or injection attempts
    const sanitized = endpoint.replace(/[<>"']/g, '');
    
    // Ensure endpoint starts with /
    return sanitized.startsWith('/') ? sanitized : `/${sanitized}`;
  }

  /**
   * Check if we have valid Supabase token from AuthCore
   */
  private async isTokenValid(): Promise<boolean> {
    try {
      const token = await AuthCore.getInstance().getAccessToken();
      return !!token;
    } catch (error) {
      return false;
    }
  }

  /**
   * Make authenticated request to backend with retry mechanism for Lambda cold start
   */
  public async request<T = any>(
    endpoint: string, 
    config: RequestConfig = {}
  ): Promise<BackendResponse<T>> {
    const { method = 'GET', body, headers = {}, requireAuth = true, signal } = config;
    
    try {
      // Sanitize endpoint
      const sanitizedEndpoint = this.sanitizeEndpoint(endpoint);
      
      // Validate authentication if required
      if (requireAuth && !(await this.isTokenValid())) {
        return {
          success: false,
          error: 'Authentication required - invalid or expired token'
        };
      }
      
      // Retry configuration for Lambda cold start
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second
      const requestTimeout = 30000; // 30 seconds
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Build full URL
          const fullUrl = `${this.baseUrl}${sanitizedEndpoint}`;
          // Making request to backend
          
          // Create AbortController for timeout or use external signal
          const controller = signal ? new AbortController() : new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), requestTimeout);
          
          // If external signal is provided, abort when it's aborted
          if (signal) {
            signal.addEventListener('abort', () => controller.abort());
          }
          
          try {
            // Prepare headers
            const requestHeaders: Record<string, string> = {
              'Content-Type': 'application/json',
              ...headers
            };
            
            // Add Supabase token if authentication is required
            if (requireAuth) {
              const token = await AuthCore.getInstance().getAccessToken();
              if (token) {
                requestHeaders['Authorization'] = `Bearer ${token}`;
              }
            }
            
            // Prepare fetch options
            const fetchOptions: RequestInit = {
              method,
              headers: requestHeaders,
              signal: controller.signal
            };
            
            // Add body for POST/PUT requests
            if ((method === 'POST' || method === 'PUT') && body) {
              fetchOptions.body = JSON.stringify(body);
            }
            
            const response = await fetch(fullUrl, fetchOptions);
            
            clearTimeout(timeoutId);
                
            // Handle response
            if (!response.ok) {
              if (response.status === 401) {
                // Handle authentication error without forcing logout
                // Let the calling code decide what to do with auth errors
                throw new Error('Authentication required');
              }
              
              throw new Error(`Request failed with status ${response.status}`);
            }
            
            const data = await response.json();
            
            // Ensure we return proper BackendResponse format
            if (data && typeof data === 'object' && 'success' in data) {
              // Backend already returned proper format
              return data;
            } else {
              // Wrap raw data in BackendResponse format
              return {
                success: true,
                data: data
              };
            }
            
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            throw fetchError;
          }
          
        } catch (error: any) {
          // Handle different types of errors
          let errorMessage = 'Request failed';
          let shouldRetry = false;
          
          if (error.name === 'AbortError') {
            errorMessage = 'Request timeout - Lambda may be cold starting';
            shouldRetry = true;
          } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network error - Lambda may be cold starting or unavailable';
            shouldRetry = true;
          } else if (error.name === 'TypeError' && error.message.includes('CORS')) {
            errorMessage = 'CORS error - backend server configuration issue';
            shouldRetry = false;
          } else if (error.message) {
            errorMessage = error.message;
            shouldRetry = error.message.includes('timeout') || error.message.includes('network');
          }
          
          // If this is the last attempt or error shouldn't be retried, return error
          if (attempt === maxRetries || !shouldRetry) {
            return {
              success: false,
              error: `${errorMessage} (after ${attempt} attempts)`
            };
          }
          
          // Wait before retry (exponential backoff)
          const delay = retryDelay * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      // This should never be reached, but just in case
      return {
        success: false,
        error: 'Maximum retry attempts exceeded'
      };
      
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Request failed'
      };
    }
  }

  public async loginWithBackend(): Promise<BackendResponse<{ token: string }>> {
    // Get Supabase access token
    const supabaseToken = await AuthCore.getInstance().getAccessToken();
    
    if (!supabaseToken) {
      return {
        success: false,
        error: 'No Supabase token available'
      };
    }
    
    // Return the Supabase token directly
    return {
      success: true,
      data: { token: supabaseToken }
    };
  }

  /**
   * Get user credits from profile
   */
  public async getUserCredits(): Promise<BackendResponse<{ credits: number }>> {
    const response = await this.request<{ user: { credits: number } }>('/user/profile');
    if (response.success && response.data?.user) {
      return {
        success: true,
        data: { credits: response.data.user.credits }
      };
    }
    return {
      success: false,
      error: response.error || 'Failed to get user credits'
    };
  }

  /**
   * DEPRECATED: Direct file upload not supported
   * Use presigned URL flow instead: getPresignedUrl() -> upload to S3 -> confirmUpload()
   */
  public async uploadFile(): Promise<BackendResponse<any>> {
    return {
      success: false,
      error: 'Direct upload not supported. Use presigned URL flow instead.'
    };
  }

  /**
   * Get upload presigned URL
   */
  public async getPresignedUrl(fileName: string, fileSize: number, mimeType: string): Promise<BackendResponse<any>> {
    try {
      // Validate inputs
      this.validateInput(fileName, 'string', 'fileName');
      this.validateInput(fileSize, 'number', 'fileSize');
      this.validateInput(mimeType, 'string', 'mimeType');
      
      return this.request('/upload/temp-urls', {
        method: 'POST',
        body: { fileName, fileSize, mimeType }
      });
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Invalid input parameters'
      };
    }
  }

  /**
   * Confirm upload completion
   */
  public async confirmUpload(uploadId: string): Promise<BackendResponse<any>> {
    try {
      // Validate input
      this.validateInput(uploadId, 'string', 'uploadId');
      
      return this.request('/upload/confirm', {
        method: 'POST',
        body: { uploadId }
      });
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Invalid uploadId'
      };
    }
  }

  /**
   * Health check (no auth required)
   */
  public async healthCheck(): Promise<BackendResponse<{ status: string }>> {
    return this.request('/health', {
      requireAuth: false
    });
  }
}

// Export singleton instance
export const backendAuthService = BackendAuthService.getInstance();

// Export types
export type { BackendResponse, RequestConfig };

// Prevent modifications
// Removed Object.freeze to allow property mutations like jwtToken updates